/**
 * Chat with the AGIMA Bot about this meeting, from inside the call. Same
 * endpoint as the admin page: the backend relays the bot's SSE stream as is
 * ({status} frames while it works, then {delta} and {done, reply}, or {error}).
 */

export interface ChatChip {
  id: string;
  label: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  // Who asked (a user message) or whom the bot answered (an assistant one):
  // the feed of a meeting is shared by everyone in the call.
  login?: string | null;
  name?: string | null;
  mine?: boolean;
  text: string;
  chip?: string | null;
  error?: string | null;
  durationMs?: number | null;
  createdAt: string;
}

export interface MeetingChatState {
  enabled: boolean;
  available: boolean;
  canManage?: boolean;
  assistantEnabled?: boolean;
  hasAgenda?: boolean;
  chips: ChatChip[];
  messages: ChatMessage[];
}

export interface ChatRequest {
  text?: string;
  chip?: string;
  chipInput?: string;
}

export interface ChatStatus {
  kind: string;
  label: string;
}

export class ChatError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

const ERROR_TEXT: Record<string, string> = {
  busy: 'Сейчас много запросов к AGIMA Bot, попробуйте через минуту.',
  agent_turn_failed: 'AGIMA Bot не смог ответить. Попробуйте ещё раз.',
  stream_interrupted: 'Ответ оборвался. Попробуйте ещё раз.',
  unavailable: 'AGIMA Bot недоступен.',
  aborted: 'Остановлено.',
};

export function chatErrorText(code: string): string {
  return ERROR_TEXT[code] || code;
}

/** «Запустить помощника»: turns it on and, the call being live, it joins now. */
export async function startMeetingAssistant(meetingId: string): Promise<void> {
  const response = await fetch(`/api/meetings/${meetingId}/assistant/start`, {
    method: 'POST',
    credentials: 'same-origin',
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
}

export async function fetchMeetingChat(meetingId: string): Promise<MeetingChatState> {
  const response = await fetch(`/api/meetings/${meetingId}/assistant-chat`, { credentials: 'same-origin' });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * Send one message and follow the stream. Resolves with the bot's reply;
 * rejects with ChatError on {error} or a broken stream, with AbortError when
 * `signal` fires.
 */
export async function sendMeetingChat(
  meetingId: string,
  request: ChatRequest,
  { signal, onStatus }: { signal?: AbortSignal; onStatus?: (status: ChatStatus) => void } = {},
): Promise<string> {
  const response = await fetch(`/api/meetings/${meetingId}/assistant-chat`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify(request),
    signal,
  });
  const type = response.headers.get('content-type') || '';
  if (!type.includes('text/event-stream')) {
    const err = await response.json().catch(() => ({}));
    const code =
      response.status === 503 && /busy/i.test(err.error || '') ? 'busy' : err.error || `HTTP ${response.status}`;
    throw new ChatError(code, chatErrorText(code));
  }
  if (!response.body) throw new ChatError('stream_interrupted', chatErrorText('stream_interrupted'));

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let cut: number;
    while ((cut = buffer.search(/\r?\n\r?\n/)) !== -1) {
      const raw = buffer.slice(0, cut);
      buffer = buffer.slice(buffer[cut] === '\r' ? cut + 4 : cut + 2);
      const data = raw
        .split(/\r?\n/)
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).replace(/^ /, ''))
        .join('\n');
      if (!data) continue;
      let frame: { status?: ChatStatus; done?: boolean; reply?: string; error?: string; detail?: string };
      try {
        frame = JSON.parse(data);
      } catch {
        continue;
      }
      if (frame.status && onStatus) onStatus(frame.status);
      if (frame.error) {
        const code = String(frame.error);
        throw new ChatError(code, chatErrorText(code));
      }
      if (frame.done) return frame.reply || '';
    }
  }
  throw new ChatError('stream_interrupted', chatErrorText('stream_interrupted'));
}
